
import { useState } from "react";
import {
  Drawer,
  Box,
  IconButton,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import { useFormik } from "formik";
import profilecross from "../../../../assets/profilecross.svg";
import CustomText, { textStyles } from "../../../../components/CustomText";
import { useTranslation } from "react-i18next";

const AddPayrollDrawer = ({ open, onClose }) => {
  const { t } = useTranslation();
  
  const formik = useFormik({
    initialValues: {
      worker: "",
      paymentType: "",
      amount: "",
      currency: "Dollar",
      date: "",
      comment: "",
      proof: null,
    },
    validate: (values) => {
      const errors = {};
      
      // Worker
      if (!values.worker || values.worker.trim() === "") {
        errors.worker = t("payrollRecords.validation.workerRequired") || "Worker is required";
      }
      
      // Payment Type
      if (!values.paymentType || values.paymentType.trim() === "") {
        errors.paymentType = t("payrollRecords.validation.paymentTypeRequired") || "Payment type is required";
      }
      
      // Amount
      if (!values.amount || values.amount.trim() === "") {
        errors.amount = t("payrollRecords.validation.amountRequired") || "Amount is required";
      } else if (isNaN(values.amount) || parseFloat(values.amount) <= 0) {
        errors.amount = t("payrollRecords.validation.amountInvalid") || "Amount must be a valid positive number";
      }
      
      // Date
      if (!values.date || values.date.trim() === "") {
        errors.date = t("payrollRecords.validation.dateRequired") || "Date is required";
      }
      
      return errors;
    },
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      console.log("Saved data: ", values);
      onClose();
    },
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 700,
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #E0E0E0",
          backgroundColor: "#f5f6f7",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 18,
              fontWeight: 600,
              color: "#172B4D",
            }}
          >
            {t("payrollRecords.addHeading") || "Add Payroll Records"}
          </CustomText>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
        <CustomText
          sx={{
            ...textStyles.h1,
            fontSize: 16,
            fontWeight: 600,
            mb: 2,
            color: "#172B4D",
          }}
        >
          {t("payrollRecords.detailsHeading") || "Payroll Details"}
        </CustomText>

        {/* Worker + Payment Type */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Worker *"
              name="worker"
              value={formik.values.worker}
              onChange={(e) => {
                formik.setFieldValue("worker", e.target.value);
                formik.setFieldTouched("worker", true);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.worker && Boolean(formik.errors.worker)}
              helperText={formik.touched.worker && formik.errors.worker}
            >
              <MenuItem value="Jane Rotanson">Jane Rotanson</MenuItem>
              <MenuItem value="Lisa Michel">Lisa Michel</MenuItem>
              <MenuItem value="Michal Mike">Michal Mike</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ flex: 1 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Payment Type *"
              name="paymentType"
              value={formik.values.paymentType}
              onChange={(e) => {
                formik.setFieldValue("paymentType", e.target.value);
                formik.setFieldTouched("paymentType", true);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.paymentType && Boolean(formik.errors.paymentType)}
              helperText={formik.touched.paymentType && formik.errors.paymentType}
            >
              <MenuItem value="Salary">Salary</MenuItem>
              <MenuItem value="Expense">Expense</MenuItem>
            </TextField>
          </Box>
        </Box>

        {/* Amount + Date */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ display: "flex", flex: 1, gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Amount *"
              name="amount"
              value={formik.values.amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.amount && Boolean(formik.errors.amount)}
              helperText={formik.touched.amount && formik.errors.amount}
              InputProps={{
                startAdornment: <span style={{ marginRight: 6 }}>$</span>,
              }}
            />
            <TextField
              select
              size="small"
              name="currency"
              value={formik.values.currency}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              sx={{ minWidth: 100 }}
            >
              <MenuItem value="Dollar">Dollar</MenuItem>
              <MenuItem value="Euro">Euro</MenuItem>
              <MenuItem value="PKR">PKR</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date *"
              name="date"
              InputLabelProps={{ shrink: true }}
              value={formik.values.date}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.date && Boolean(formik.errors.date)}
              helperText={formik.touched.date && formik.errors.date}
            />
          </Box>
        </Box>

        {/* Comment */}
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Comment"
          name="comment"
          value={formik.values.comment}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          sx={{ mb: 3 }}
        />

        {/* Supporting Proof */}
        <CustomText
          sx={{
            fontSize: 14,
            fontWeight: 500,
            mb: 1,
            color: "#172B4D",
            textAlign: "left",
          }}
        >
          Supporting Proof
        </CustomText>
        <Box
          sx={{
            border: "1px dashed #D0D5DD",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            backgroundColor: "#F9FAFB",
            cursor: "pointer",
            mb: 3,
          }}
        >
          <label htmlFor="proof-upload" style={{ cursor: "pointer" }}>
            <input
              type="file"
              id="proof-upload"
              name="proof"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                formik.setFieldValue("proof", file);
              }}
              hidden
            />
            <CustomText sx={{ fontSize: 14, fontWeight: 500 }}>
              📎 Drag files here or click to{" "}
              <span style={{ textDecoration: "underline", color: "#006EC2" }}>
                browse
              </span>
            </CustomText>
          </label>
        </Box>

        {/* Save button */}
        <Button
          variant="contained"
          onClick={formik.handleSubmit}
          sx={{
            backgroundColor: "#006EC2",
            textTransform: "none",
            fontFamily: "Poppins, sans-serif",
            px: 3,
          }}
        >
          {t("payrollRecords.actions.save") || "Save"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default AddPayrollDrawer;

